import { Anchor, Space, Box, LoadingOverlay, Group as MantineGroup } from "@mantine/core";
import { useEffect, useMemo } from "react";
import { NavLink, useParams } from "react-router-dom";
import BreadcrumbsNavigation from "@/components/breadcrumbs-navigation";
import CardNomeEmpresa from "@/modules/etapa-processo/components/cards/CardNomeEmpresa";
import CardPremio from "@/modules/etapa-processo/components/cards/CardPremio";
import CardResultados from "@/modules/etapa-processo/components/cards/CardResultados";
import useGetProjetoFromUrl from "@/modules/etapa-processo/components/cards/useGetProjetoFromUrl";
import EstrategiaList from "@/modules/etapa-processo/components/estrategia-list/EstrategiaList";
import ResumoAvaliacao from "@/modules/etapa-processo/components/estrategia-list/ResumoAvaliacao";
import { useAcessoQuestionario } from "@/modules/etapa-processo/components/estrategia-list/useAcessoQuestionario";
import useQueryGroup from "@/modules/etapa-processo/components/estrategia-list/useGetGroup";
import useGetQuestionarioFromUrl from "@/modules/etapa-processo/components/estrategia-list/useGetQuestionarioFromUrl";
import usePendingNotasStore from "@/modules/etapa-processo/stores/usePendingNotasStore";
import { useGetResultEmpresa } from "@/modules/media-grupo/hooks/useGetResultEmpresa";
import useGetProjetoById from "@/modules/projetos/hooks/useGetProjetoById";
import { ReportTemplatePicker, useReportData } from "@/modules/relatorios";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import useGetQuestionarioItem from "@/src/routes/responder-questionario-externo-page/hooks/useGetQuestionarioFromUrl";
import useRefreshStore from "@/utils/useRefreshStore";

type QueryGroupData = NonNullable<ReturnType<typeof useQueryGroup>["data"]>;
type Grupo = QueryGroupData[number];
type ProjetoByIdData = ReturnType<typeof useGetProjetoById>["data"];
type ProjetoData = ProjetoByIdData;
type QuestionarioByIdData = ReturnType<typeof useGetQuestionarioItem>["data"];
type QuestionarioData = QuestionarioByIdData;
type ReportParameters = Parameters<typeof useReportData>[0];
type ReportResults = Parameters<typeof useReportData>[1];
type AcessoQuestionarioPayload = Parameters<typeof useAcessoQuestionario>[0];
type ResultData = Pick<
  ReturnType<typeof useGetResultEmpresa>,
  "resultEmpresa" | "resultGeral" | "resultCategoria" | "resultPrimeiroLugar"
>;

type RadarLoadArguments = {
  questionarioId: string;
  projetoId: string;
  empresaId: string;
  grupos: Grupo[];
};

const toSafeString = (value?: string | null) => value ?? "";

const toUndefined = <T,>(value: T | null | undefined): T | undefined => value ?? undefined;

const getCurrentUserId = () => getCurrentUser()?.uid ?? "";

const getGruposOrdenados = (data: QueryGroupData | undefined): Grupo[] => {
  if (!data) {
    return [];
  }

  const gruposRadar = data.filter((grupo) => grupo.radar);
  return gruposRadar.sort((a, b) => a.ordemQuestionario - b.ordemQuestionario);
};

const resolveByEmpresaProjetoId = <T,>(
  empresaProjetoId: string | undefined,
  byId: T | null | undefined,
  byUrl: T | null | undefined
): T | null | undefined => {
  if (empresaProjetoId) {
    return byId;
  }

  return byUrl;
};

const buildReportParameters = (parameters: {
  id?: string;
  idEmpresa?: string;
  idProjeto?: string;
  idEmpresaProjeto?: string;
  questionario: QuestionarioData | null | undefined;
  projeto: ProjetoData | null | undefined;
}): ReportParameters => ({
  questionarioId: toSafeString(parameters.id),
  empresaId: toSafeString(parameters.idEmpresa),
  projetoId: toSafeString(parameters.idProjeto),
  empresaProjetoId: toSafeString(parameters.idEmpresaProjeto),
  questionario: toUndefined(parameters.questionario),
  projeto: toUndefined(parameters.projeto)
});

const buildReportResults = (results: ResultData): ReportResults => ({
  empresa: results.resultEmpresa ?? [],
  geral: results.resultGeral,
  categoria: results.resultCategoria,
  primeiroLugar: results.resultPrimeiroLugar
});

const buildAcessoQuestionarioPayload = (parameters: {
  id?: string;
  idProjeto?: string;
  idEmpresa?: string;
  currentUserId: string;
}): AcessoQuestionarioPayload => ({
  idQuestionario: toSafeString(parameters.id),
  idProjeto: toSafeString(parameters.idProjeto),
  updatedAt: new Date(),
  deletedAt: null,
  createdBy: parameters.currentUserId,
  updatedBy: parameters.currentUserId,
  idEmpresa: toSafeString(parameters.idEmpresa)
});

const getRadarLoadArguments = (
  questionario: QuestionarioData | null | undefined,
  projeto: ProjetoData | null | undefined,
  idEmpresa: string | undefined,
  grupos: Grupo[]
): RadarLoadArguments | null => {
  if (!questionario || !projeto || !idEmpresa || grupos.length === 0) {
    return null;
  }

  return {
    questionarioId: questionario.id,
    projetoId: projeto.id,
    empresaId: idEmpresa,
    grupos
  };
};

const renderPremioCard = (empresaProjetoId: string | undefined) => {
  if (empresaProjetoId) {
    return null;
  }

  return <CardPremio empresaProjetoId={toSafeString(empresaProjetoId)} />;
};

const QuestionarioInfoPage = () => {
  const refreshKey = useRefreshStore((state) => state.refreshKey);
  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);

  const { id, idProjeto, idEmpresa, idEmpresaProjeto } = useParams<{
    id: string;
    idProjeto: string;
    idEmpresa: string;
    idEmpresaProjeto: string;
  }>();

  const { data, isFetching } = useQueryGroup(idEmpresaProjeto);
  const currentUserId = getCurrentUserId();
  const grupos = useMemo(() => getGruposOrdenados(data), [data]);

  // Hooks para obter dados dos gráficos radar
  const projetoByIdResult = useGetProjetoById(toSafeString(idEmpresaProjeto), toSafeString(idProjeto), licenciadaId);
  const questionarioByIdResult = useGetQuestionarioItem(toSafeString(idEmpresaProjeto), toSafeString(id));
  const projetoFromUrlResult = useGetProjetoFromUrl();
  const questionarioFromUrlResult = useGetQuestionarioFromUrl();

  const projeto = resolveByEmpresaProjetoId(idEmpresaProjeto, projetoByIdResult.data, projetoFromUrlResult.data);
  const questionario = resolveByEmpresaProjetoId(
    idEmpresaProjeto,
    questionarioByIdResult.data,
    questionarioFromUrlResult.data
  );

  const { load, resultEmpresa, resultGeral, resultCategoria, resultPrimeiroLugar } =
    useGetResultEmpresa(idEmpresaProjeto);

  // Hook para consolidar dados do relatório
  const reportParameters = buildReportParameters({
    id,
    idEmpresa,
    idProjeto,
    idEmpresaProjeto,
    questionario,
    projeto
  });
  const reportResults = buildReportResults({
    resultEmpresa,
    resultGeral,
    resultCategoria,
    resultPrimeiroLugar
  });
  const { data: reportData, isLoading: isLoadingReportData } = useReportData(reportParameters, reportResults);

  const acessoQuestionarioPayload = buildAcessoQuestionarioPayload({
    id,
    idProjeto,
    idEmpresa,
    currentUserId
  });
  const { mutate } = useAcessoQuestionario(acessoQuestionarioPayload);

  useEffect(() => {
    mutate();
  }, [id, mutate]);

  // Carrega dados dos gráficos radar
  useEffect(() => {
    const radarLoadArguments = getRadarLoadArguments(questionario, projeto, idEmpresa, grupos);
    if (!radarLoadArguments) {
      return;
    }

    void load(
      radarLoadArguments.questionarioId,
      radarLoadArguments.projetoId,
      radarLoadArguments.empresaId,
      radarLoadArguments.grupos
    );
  }, [questionario?.id, projeto?.id, idEmpresa, load]);

  // Restaura notas pendentes do localStorage ao montar o componente
  useEffect(() => {
    const userId = getCurrentUser()?.uid;
    if (userId) {
      usePendingNotasStore.getState().loadFromLocalStorage(userId);
    }
  }, []);

  // Avisa o usuário ao tentar sair da página com notas pendentes
  useEffect(() => {
    const handleBeforeUnload = (error: BeforeUnloadEvent) => {
      const hasPending = usePendingNotasStore.getState().hasPendingChanges();
      if (hasPending) {
        error.preventDefault();
        error.returnValue = ""; // Chrome requer isso
        return ""; // Para navegadores antigos
      }
      return undefined;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  if (isFetching) {
    return (
      <Box pos="relative" h="100vh">
        <LoadingOverlay visible zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
      </Box>
    );
  }

  const premioCard = renderPremioCard(idEmpresaProjeto);

  return (
    <>
      <MantineGroup justify="space-between" align="center" wrap="nowrap">
        <BreadcrumbsNavigation notHome>
          <Anchor component={NavLink} to="/diagnostico">
            Diagnóstico
          </Anchor>
        </BreadcrumbsNavigation>

        <ReportTemplatePicker data={reportData} disabled={isLoadingReportData || !reportData} />
      </MantineGroup>

      <div>
        <Space h="md" />
        <CardNomeEmpresa />
        <Space h="md" />
        {premioCard}
        <Space h="md" />
        <CardResultados key={refreshKey} grupos={grupos} />
        <Space h="md" />
        {grupos.map((item) => (
          <EstrategiaList key={item.id} grupo={item} empresaProjetoId={idEmpresaProjeto} />
        ))}
        <Space h="xl" />
        <ResumoAvaliacao grupos={grupos} />
      </div>
    </>
  );
};

QuestionarioInfoPage.displayName = "QuestionarioInfoPage";

export default QuestionarioInfoPage;
